import { useEffect, useMemo, useState, type CSSProperties, type Dispatch, type SetStateAction } from 'react';
import { capacityDisplay } from '../../lib/classCapacity';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus, Archive, RotateCcw, MoreVertical, Pencil, Users, ChevronDown, X, Info, Layers, Check, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { cn } from '@repo/ui/src/lib/utils';
import { Button } from '@repo/ui/src/components/ui/button';
import { Badge } from '@repo/ui/src/components/ui/badge';
import { Checkbox } from '@repo/ui/src/components/ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@repo/ui/src/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@repo/ui/src/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@repo/ui/src/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@repo/ui/src/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@repo/ui/src/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@repo/ui/src/components/ui/popover';
import type { ClassSlotWithDetail, ScheduleTemplateDetail, EnrollmentWithSubject } from '@repo/shared-types';
import { useSlots, useArchiveSlot, useUnarchiveSlot, useMoveSlotMembers, useJoinSlot, useLeaveSlot } from '../../hooks/useSlots';
import { markChipHintLearned, useChipAffordance } from '../../lib/chipAffordance';
import { useScheduleTemplates, useScheduleTemplate, useSaveTemplateEntries } from '../../hooks/useScheduleTemplates';
import { useShowTeacher, useTeacherOptions } from '../../hooks/useTeachers';
import { useMembers } from '../../hooks/useTeam';
import { useCan } from '../../hooks/useCan';
import { useServerNow } from '../../hooks/useServerNow';
import { useEnrollmentsForSubject } from '../../hooks/useEnrollmentsForSubject';
import { AdminOnlyTooltip } from '../../components/AdminOnlyTooltip';
import { pageHeaderActionClass } from '../../components/PageHeader';
import { AddStudentCombobox } from '../../components/AddStudentCombobox';
import { resolveTeacher, type TeacherLike } from '../../lib/teacherDisplay';
import { classColorStyle } from '../../lib/classColors';
import { displayNames } from '../../lib/studentName';
import { SelectionBar } from '../../components/SelectionBar';
import { overlayTemplateOnSlots, applyMoveToEntries, removeEntryFromEntries, addEntryToEntries } from '../../lib/templateEntries';
import { formatMonthKey, reviewMonthFor } from '../../lib/sessionTime';
import {
  emptyMoveDraft,
  toggleTick,
  canTickInSlot,
  isEligibleTarget,
  setTarget,
  clearDraft,
  type SlotMoveDraft,
  type SlotMoveCandidate,
} from '../../lib/slotMoveDraft';
import { SlotFormDialog } from '../schedules/SlotFormDialog';
import { TemplateBanner } from '../schedules/TemplateBanner';
import { CreateTemplateDialog, RenameTemplateDialog, DeleteTemplateDialog } from '../schedules/TemplateDialogs';
import { ApplyTemplateDialog } from '../schedules/ApplyTemplateDialog';
import { fmtAppliedDate } from '../../lib/appliedDate';
import { useEffectiveBranchId } from '../../hooks/useBranchScope';

/** A slot as the grid renders it — real `ClassSlotWithDetail` in reality
 *  mode, or the same shape with `members`/`memberCount` swapped for a
 *  template's entries (see `overlayTemplateOnSlots`). `templateOrphan` is
 *  only ever `true` for an archived slot a template still points at. */
type GridSlot = ClassSlotWithDetail & { templateOrphan?: boolean };

// dnd-kit only cares about id/subjectId/status — this strips a full
// ClassSlotWithDetail down to what slotMoveDraft.ts (a pure lib with no
// dependency on shared-types) actually needs.
function toCandidate(s: ClassSlotWithDetail): SlotMoveCandidate {
  return { id: s.id, subjectId: s.subjectId, status: s.status };
}

// Drag is a desktop-only affordance (see design doc: "a 7-column grid that
// scrolls under a finger is not a drag surface") — mobile gets the sheet
// picker only. `lg` (1024px) matches the breakpoint the grid itself already
// switches to 7 columns at.
function useIsDesktop(): boolean {
  const [isDesktop, setIsDesktop] = useState(() => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const onChange = () => setIsDesktop(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return isDesktop;
}

const DAYS = [1, 2, 3, 4, 5, 6, 7];
const ALL_TEACHERS = '__all__';

// getDay(): Sun=0..Sat=6 -> app scheme Mon=1..Sun=7
function currentWeekday(): number {
  const d = new Date().getDay();
  return d === 0 ? 7 : d;
}

/**
 * "Lịch cố định hàng tuần" tab (S3) — today's `TimetablePage` body, moved
 * verbatim (grid, move mode, DnD, template banner, one-off dialogs, slot
 * detail dialog all intact), with three changes for the class layer:
 *
 * 1. Cards lead with `slot.group.displayName` and tint by `slot.group.color`
 *    (`classColorStyle`) instead of the bare subject name.
 * 2. `moveMode` is now a PROP — the toggle button that flips it lives in
 *    ClassesPage's shared header (S2/S3 render the identical header), not
 *    here. The effect below reproduces the old local `enterMoveMode` /
 *    `exitMoveMode` side effects (force ACTIVE view, clear the draft)
 *    whenever the prop changes instead of when a local setState fires.
 * 3. "Mẫu lịch học" moved from the page header into this tab's own filter
 *    row (handoff decision 5 — it changes the grid you're looking at, not
 *    the page), and the ACTIVE/ARCHIVED toggle + teacher filter live here
 *    too, alongside the new one-line "not buổi" note above the grid.
 */
export function TimetableGridTab({ moveMode, onExitMoveMode }: {
  moveMode: boolean;
  onExitMoveMode: () => void;
}) {
  const { t } = useTranslation('scheduling');
  const { t: tCommon } = useTranslation('common');
  const [view, setView] = useState<'ACTIVE' | 'ARCHIVED'>('ACTIVE');
  const showTeacher = useShowTeacher();
  const teacherOptions = useTeacherOptions();
  // includeRemoved: an ARCHIVED slot may belong to a since-removed teacher —
  // their name must still resolve here (the filter dropdown itself uses the
  // active-only teacherOptions, so a removed teacher can't be filtered by).
  const { data: members } = useMembers({ includeRemoved: true });
  const [teacherFilter, setTeacherFilter] = useState<string | undefined>(undefined);
  const branchId = useEffectiveBranchId();
  const { data: slots, isLoading } = useSlots({ status: view, teacherId: showTeacher ? teacherFilter : undefined, branchId });
  const canManageSlots = useCan('manage:slots');

  // "Mẫu lịch học" — null = "Thực tế (đang áp dụng)". Template mode is
  // purely additive: with `templateId === null` every expression below is
  // byte-for-byte what it was before this feature existed. Never fetched
  // for a MEMBER (`enabled: canManageSlots` — see useCan gate above).
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [templateCreateOpen, setTemplateCreateOpen] = useState(false);
  const [templateRenameOpen, setTemplateRenameOpen] = useState(false);
  const [templateDeleteOpen, setTemplateDeleteOpen] = useState(false);
  const [templateApplyOpen, setTemplateApplyOpen] = useState(false);
  const { data: templates } = useScheduleTemplates({ enabled: canManageSlots });
  const { data: template, isError: templateIsError } = useScheduleTemplate(templateId);
  const { data: archivedSlots } = useSlots(
    { status: 'ARCHIVED', teacherId: showTeacher ? teacherFilter : undefined, branchId },
    { enabled: templateId !== null },
  );
  const saveEntries = useSaveTemplateEntries(templateId ?? '');
  const isTemplateMode = templateId !== null;
  // Neither reality (nothing to wait for) nor a template whose detail has
  // actually arrived — while false the grid must not show real memberships
  // under a template-labelled header, and move mode must stay unreachable
  // (see the `grid`/`hidden` uses below).
  const templateReady = templateId === null || !!template;

  // A failed template fetch must never leave the page labelled-but-real: no
  // banner, a skeleton in place of the grid (via `templateReady` above), and
  // this resets the select back to reality so the broken state can't persist.
  useEffect(() => {
    if (templateId !== null && templateIsError) {
      toast.error(t('template.loadError'));
      // The setState here is intentional — a failed query is the external
      // signal we react to, resetting the select back to reality so the
      // labelled-but-real state can't persist.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setTemplateId(null);
    }
  }, [templateId, templateIsError, t]);

  const [createOpen, setCreateOpen] = useState(false);
  const [editSlot, setEditSlot] = useState<ClassSlotWithDetail | null>(null);
  // Just the id — the object itself is looked up fresh from `gridSlots` on
  // every render (below), not frozen at the moment the card was clicked. The
  // dialog now mutates membership (add/remove), and a snapshot object would
  // never reflect its own edit: a remove would still show the removed member
  // until the dialog was closed and reopened.
  const [detailSlotId, setDetailSlotId] = useState<string | null>(null);
  // Sibling outline on hover (S3 decision, desktop only): the id of the
  // group currently under the pointer — every card sharing that group.id
  // gets the "these belong together" ring, the hovered card itself a
  // stronger one. Deliberately NOT wired to tap (move mode already owns
  // touch's tick gesture, and an ordinary card's tap opens its detail
  // dialog — a second tap meaning would collide with that).
  const [hoveredGroupId, setHoveredGroupId] = useState<string | null>(null);
  const today = currentWeekday();

  // "Chế độ xếp lớp" — see docs/superpowers/specs/2026-08-14-slot-move-mode-design.md.
  // Outside this mode the screen above is untouched; moveMode only changes
  // what the grid renders below. Draft rules (tick/target/clear) all live in
  // slotMoveDraft.ts — this component only calls them.
  const [draft, setDraft] = useState<SlotMoveDraft>(emptyMoveDraft);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const moveMutation = useMoveSlotMembers();
  const isDesktop = useIsDesktop();
  const dndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  // Reproduces the old local `enterMoveMode`/`exitMoveMode`'s side effects —
  // force the ACTIVE view and clear any draft — now driven by the `moveMode`
  // PROP (the toggle that flips it lives in ClassesPage's header) rather than
  // a local setState call.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setDraft(clearDraft());
    if (moveMode) setView('ACTIVE');
  }, [moveMode]);

  // Picking (or creating) a template forces move mode off — a template edit
  // and a live bulk-move draft must never be in flight at once. Only the
  // LOCAL draft is this component's to clear; the boolean itself is the
  // parent's, so this calls back up.
  const forceExitMoveMode = () => { setDraft(clearDraft()); onExitMoveMode(); };

  // Picking a template (from reality or from another template) forces the
  // ACTIVE view and exits move mode — the archived *view* toggle and
  // template mode are two different things and must not compose (see the
  // ARCHIVED toggle's `disabled` below). Returning to reality is the
  // banner's own "Thoát" button, not this menu — see TemplateBanner's
  // `onExit`.
  const handlePickTemplate = (id: string) => {
    setTemplateId(id);
    setView('ACTIVE');
    forceExitMoveMode();
  };

  // Real ClassSlotWithDetail objects with `members`/`memberCount` swapped
  // for the template's entries — TimetableSlotCard, MoveModeSlotCard and
  // slotMoveDraft all keep working unchanged; template mode adds no second
  // rendering path. While `templateId` is set but the detail hasn't loaded
  // yet, this deliberately falls back to the real slots (see the design doc
  // wire-up) rather than an empty/loading grid.
  const gridSlots: GridSlot[] = useMemo(
    () =>
      templateId !== null && template
        ? overlayTemplateOnSlots(slots ?? [], archivedSlots ?? [], template.entries)
        : (slots ?? []),
    [templateId, template, slots, archivedSlots],
  );

  const sourceSlot = useMemo(
    () => (draft.sourceSlotId ? gridSlots.find((s) => s.id === draft.sourceSlotId) ?? null : null),
    [gridSlots, draft.sourceSlotId],
  );

  // Same reasoning as `sourceSlot` above — looked up by id every render so
  // the open dialog reflects its own membership edits immediately.
  const detailSlot = useMemo(
    () => (detailSlotId ? gridSlots.find((s) => s.id === detailSlotId) ?? null : null),
    [gridSlots, detailSlotId],
  );
  const detailTeacher = detailSlot && showTeacher ? resolveTeacher(members ?? [], detailSlot.teacherId) : null;
  // Same condition MoveModeBar itself gates rendering on — mirrored here so
  // the page container can clear space for the bar without importing its
  // internals.
  const moveBarVisible = moveMode && draft.selected.length > 0 && !!sourceSlot;

  // Drop only sets the target — same call the picker makes (setTarget), so
  // the resulting draft is identical whichever gesture the teacher used. The
  // confirm bar still gates the actual mutation.
  const handleDragEnd = (event: DragEndEvent) => {
    setActiveDragId(null);
    if (!event.over || !sourceSlot) return;
    const candidate = gridSlots.find((s) => s.id === event.over!.id);
    if (!candidate) return;
    setDraft((d) => setTarget(d, toCandidate(sourceSlot), toCandidate(candidate)));
  };
  const handleDragStart = (event: DragStartEvent) => setActiveDragId(String(event.active.id));

  const byDay = useMemo(() => {
    const m = new Map<number, GridSlot[]>();
    for (const s of gridSlots) { const a = m.get(s.dayOfWeek) ?? []; a.push(s); m.set(s.dayOfWeek, a); }
    for (const a of m.values()) a.sort((x, y) => x.startTime.localeCompare(y.startTime));
    return m;
  }, [gridSlots]);

  const totalSlots = gridSlots.length;

  // Computed once and rendered from both the DndContext-wrapped (move mode)
  // and plain branches below, so the two paths share one expression instead
  // of two copies of the grid markup that could drift apart.
  const grid = isLoading || !templateReady ? (
    <TimetableSkeleton />
  ) : totalSlots === 0 && view === 'ARCHIVED' ? (
    // The archive is not a place to teach slot creation: an empty archive is
    // good news, so it gets a quiet line and no CTA (07 empty-state kinds).
    <div className="rounded-lg border border-dashed border-surface-200 px-6 py-14 text-center">
      <h2 className="text-base font-semibold text-surface-900">{t('timetable.emptyArchived')}</h2>
      <p className="mx-auto mt-1.5 max-w-sm text-sm leading-relaxed text-surface-500">{t('timetable.emptyArchivedHint')}</p>
    </div>
  ) : totalSlots === 0 ? (
    <div className="rounded-lg border border-surface-200 bg-card px-6 py-14 text-center">
      <div className="mx-auto mb-4 flex h-11 w-11 items-center justify-center rounded-md bg-brand-50 text-brand-600">
        <Layers className="h-5 w-5" strokeWidth={1.8} />
      </div>
      <h2 className="text-base font-semibold text-surface-900">{t('timetable.empty')}</h2>
      <p className="mx-auto mt-1.5 max-w-sm text-sm leading-relaxed text-surface-500">{t('timetable.emptyHint')}</p>
      <AdminOnlyTooltip show={!canManageSlots}>
        <Button onClick={() => setCreateOpen(true)} className="mt-5 gap-2" disabled={!canManageSlots}>
          <Plus className="h-4 w-4" /> {t('timetable.newSlot')}
        </Button>
      </AdminOnlyTooltip>
    </div>
  ) : (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-x-3 gap-y-5 lg:gap-3">
      {DAYS.map((d, i) => {
        const daySlots = byDay.get(d) ?? [];
        const isToday = d === today;
        return (
          <section
            key={d}
            id={`grid-day-${d}`}
            className="motion-safe:animate-[fade-up_0.4s_cubic-bezier(0.22,1,0.36,1)_both] scroll-mt-3"
            style={{ animationDelay: `${i * 45}ms` }}
          >
            <div
              className={cn(
                'flex items-center justify-between gap-2 pb-1.5 mb-2.5 border-b',
                isToday ? 'border-brand-500/50' : 'border-surface-200',
              )}
            >
              <span
                className={cn(
                  'inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider',
                  isToday ? 'text-brand-700' : 'text-surface-400',
                )}
              >
                {isToday && <span className="inline-block h-1.5 w-1.5 rounded-full bg-brand-500" aria-hidden />}
                {t(`days.${d}`)}
                {isToday && <span className="sr-only">({t('timetable.today')})</span>}
              </span>
              {daySlots.length > 0 ? (
                <span className="tabular text-[11px] font-medium text-surface-400">{daySlots.length}</span>
              ) : null}
            </div>

            {daySlots.length > 0 ? (
              <div className="space-y-2">
                {daySlots.map((s) =>
                  moveMode ? (
                    <MoveModeSlotCard
                      key={s.id}
                      slot={s}
                      draft={draft}
                      setDraft={setDraft}
                      sourceSlot={sourceSlot}
                      teacher={showTeacher ? resolveTeacher(members ?? [], s.teacherId) : null}
                      isDesktop={isDesktop}
                      isDragActive={activeDragId !== null}
                      templateOrphan={!!s.templateOrphan}
                      templateMode={isTemplateMode}
                    />
                  ) : (
                    <TimetableSlotCard
                      key={s.id}
                      slot={s}
                      teacher={showTeacher ? resolveTeacher(members ?? [], s.teacherId) : null}
                      onOpenDetail={() => setDetailSlotId(s.id)}
                      onEdit={() => setEditSlot(s)}
                      templateOrphan={!!s.templateOrphan}
                      templateMode={isTemplateMode}
                      hoverState={
                        hoveredGroupId === null ? 'none' : s.group.id !== hoveredGroupId ? 'none' : draft.sourceSlotId === s.id ? 'none' : 'sibling'
                      }
                      isHovered={hoveredGroupId === s.group.id}
                      onHoverStart={() => setHoveredGroupId(s.group.id)}
                      onHoverEnd={() => setHoveredGroupId((g) => (g === s.group.id ? null : g))}
                    />
                  ),
                )}
              </div>
            ) : (
              <div className="rounded-md border border-dashed border-surface-200 py-3 text-center text-[12px] text-surface-300">—</div>
            )}
          </section>
        );
      })}
    </div>
  );

  return (
    <div className={cn(moveBarVisible && 'pb-40 md:pb-24')}>
      {templateId !== null && template ? (
        <TemplateBanner
          template={template}
          onApply={() => setTemplateApplyOpen(true)}
          onRename={() => setTemplateRenameOpen(true)}
          onDelete={() => setTemplateDeleteOpen(true)}
          onExit={() => setTemplateId(null)}
        />
      ) : null}

      {moveMode ? <MoveModeHint draft={draft} isDesktop={isDesktop} /> : null}

      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="inline-flex items-center gap-0.5 rounded-md border border-surface-200 bg-surface-0 p-0.5">
          {(['ACTIVE', 'ARCHIVED'] as const).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setView(v)}
              disabled={(moveMode || isTemplateMode) && v === 'ARCHIVED'}
              className={cn(
                'rounded-[3px] px-3 py-1 text-[13px] font-medium transition-colors',
                view === v ? 'bg-brand-500 text-white shadow-sm' : 'text-surface-500 hover:text-surface-800',
                (moveMode || isTemplateMode) && v === 'ARCHIVED' && 'cursor-not-allowed opacity-40 hover:text-surface-500',
              )}
            >
              {v === 'ACTIVE' ? t('slot.showActive') : t('slot.showArchived')}
            </button>
          ))}
        </div>

        {showTeacher ? (
          <Select value={teacherFilter ?? ALL_TEACHERS} onValueChange={(v) => setTeacherFilter(v === ALL_TEACHERS ? undefined : v)}>
            <SelectTrigger className="w-52 h-8"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_TEACHERS}>{t('timetable.allTeachers')}</SelectItem>
              {teacherOptions.map((opt) => (
                <SelectItem key={opt.id} value={opt.id}>
                  <span className="inline-flex items-center gap-2">
                    <span className="inline-block h-2 w-2 rounded-full" style={{ background: opt.color }} />
                    {opt.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : null}

        {/* "Mẫu lịch học" (handoff decision 5): lives in this tab's own
            filter row, not the shared page header — it changes which grid
            you're looking at, not the page. Hidden entirely for a MEMBER, no
            tooltip (same non-disclosing pattern as the move-mode toggle). */}
        {canManageSlots ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                size="sm"
                variant="outline"
                aria-label={t('template.label')}
                className={cn(pageHeaderActionClass, 'gap-2')}
              >
                <Layers className="h-4 w-4" /> {t('template.label')}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              {(templates ?? []).map((tpl) => (
                <DropdownMenuItem
                  key={tpl.id}
                  onClick={() => handlePickTemplate(tpl.id)}
                  className="flex cursor-pointer items-center justify-between gap-2"
                >
                  <span className="flex min-w-0 flex-col py-0.5">
                    <span className="truncate">{tpl.name}</span>
                    {tpl.appliedAt ? (
                      <span className="text-xs text-surface-400">
                        {t('template.appliedNote', { date: fmtAppliedDate(tpl.appliedAt) })}
                      </span>
                    ) : null}
                  </span>
                  {templateId === tpl.id ? <Check className="h-4 w-4 shrink-0 text-brand-600" /> : null}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setTemplateCreateOpen(true)} className="cursor-pointer">
                {t('template.createItem')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>

      {/* Not a flex row (unlike the desktop-only S3 mock's own paragraph) —
          at 390 the sentence must wrap normally with the link inline in the
          text, not hold the link as its own flex item shrunk into a stack of
          single words. */}
      <p className="mb-3 text-[13px] text-surface-500">
        <Info className="mr-1.5 inline-block h-3.5 w-3.5 -translate-y-0.5" strokeWidth={1.8} />
        {t('grid.note')} <Link to="/" className="font-medium text-brand-600 hover:underline">{tCommon('nav.dashboard')}</Link>.
      </p>

      {/* Fix wave (screenshot gate S3/3) — weekday quick-jump chip strip,
          390 only (`sm:hidden`; the grid is already a single column below
          `sm`, so a phone otherwise has to scroll past every day's cards to
          reach Chủ nhật). Tap scrolls that day's `<section id="grid-day-N">`
          into view; today's chip is highlighted the same way the section's
          own heading already is. */}
      {totalSlots > 0 ? (
        <div className="mb-3 flex gap-[6.4px] overflow-x-auto sm:hidden" role="tablist" aria-label={t('grid.jumpToDay')}>
          {DAYS.map((d) => {
            const isToday = d === today;
            return (
              <button
                key={d}
                type="button"
                role="tab"
                aria-selected={isToday}
                onClick={() => document.getElementById(`grid-day-${d}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                // Re-review fix (new defect 4, §6.1): 44px tap target — this
                // strip only ever renders below `sm`, so no responsive
                // variant needed.
                className={cn(
                  'flex h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded-full border px-[11.2px] text-[12.5px] font-semibold',
                  isToday ? 'border-brand-500 bg-brand-500 text-white' : 'border-surface-200 bg-surface-0 text-surface-600',
                )}
              >
                {t(`days.${d}`)}
              </button>
            );
          })}
        </div>
      ) : null}

      {moveMode ? (
        // DndContext only mounts in move mode — outside it, no PointerSensor
        // listens at the document level and the grid renders exactly as it
        // always has (same `grid` expression as the branch below).
        <DndContext sensors={dndSensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          {grid}
          <DragOverlay>
            {activeDragId ? (
              <Badge variant="success" className="shadow-lg">{t('moveMode.dragBadge', { count: draft.selected.length })}</Badge>
            ) : null}
          </DragOverlay>
        </DndContext>
      ) : (
        grid
      )}

      {moveMode ? (
        <MoveModeBar
          slots={gridSlots}
          draft={draft}
          setDraft={setDraft}
          sourceSlot={sourceSlot}
          moveMutation={moveMutation}
          templateId={templateId}
          template={template}
          saveEntries={saveEntries}
        />
      ) : null}

      {/* THI-267 Task 6 turned an existing (or archived) teacher+time into a 409
          instead of a merged/revived class, so `useCreateSlot`'s outcome is
          always 'created' now — there is nothing left to auto-open here. */}
      <SlotFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
      />
      <SlotFormDialog
        key={editSlot?.id ?? 'edit-none'}
        open={editSlot !== null}
        onOpenChange={(o) => { if (!o) setEditSlot(null); }}
        slot={editSlot ?? undefined}
      />
      <SlotDetailDialog
        slot={detailSlot}
        teacher={detailTeacher}
        members={members ?? []}
        open={detailSlotId !== null}
        onOpenChange={(o) => { if (!o) setDetailSlotId(null); }}
        templateId={templateId}
        template={template}
        saveEntries={saveEntries}
      />

      <CreateTemplateDialog
        open={templateCreateOpen}
        onOpenChange={setTemplateCreateOpen}
        onCreated={(id) => { setTemplateId(id); setView('ACTIVE'); forceExitMoveMode(); }}
      />
      <RenameTemplateDialog
        open={templateRenameOpen}
        onOpenChange={setTemplateRenameOpen}
        template={template ?? null}
      />
      <DeleteTemplateDialog
        open={templateDeleteOpen}
        onOpenChange={setTemplateDeleteOpen}
        template={template ?? null}
        onDeleted={() => setTemplateId(null)}
      />
      {templateId !== null ? (
        <ApplyTemplateDialog
          open={templateApplyOpen}
          onOpenChange={setTemplateApplyOpen}
          templateId={templateId}
          onApplied={() => setTemplateId(null)}
        />
      ) : null}
    </div>
  );
}

// The "slot đã lưu trữ" indicator on an orphaned template-mode card — an
// icon+text+popover instead of the icon-only triangle it replaces (that
// triangle carried no visible label and no way to act on the state besides
// reading why). Same real-button-trigger pattern as ModeExplanationPopover:
// Radix renders a real `<button>`, aria-label carries the explanation for a
// screen reader, and the 44px tap target is pinned in px (not
// spacing-derived) for touch. Same dashed-underline tap affordance as the
// attendance chips (useChipAffordance) — it teaches itself away after three
// taps of ANY such chip, this one included. Used by both TimetableSlotCard
// and MoveModeSlotCard's orphan branch — the only two places a card can be
// `templateOrphan`. Carries its own restore action: confirming unarchives
// the slot via the same mutation SlotActionsMenu uses, and the orphan state
// resolves on its own once the invalidated `slots` query refetches.
function OrphanSlotPopover({ slotId }: { slotId: string }) {
  const { t } = useTranslation('scheduling');
  const [open, setOpen] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const unarchive = useUnarchiveSlot();
  const affordance = useChipAffordance();

  return (
    <>
      <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v) markChipHintLearned(); }}>
        <PopoverTrigger
          aria-label={t('template.orphanPopover.aria')}
          className="absolute right-1 top-1 z-10 inline-flex min-h-[44px] items-center gap-1 px-1 text-attention-foreground"
        >
          <AlertTriangle className="h-4 w-4 shrink-0" strokeWidth={1.9} />
          <span className={cn('text-[12px] font-medium', affordance)}>{t('template.orphanPopover.label')}</span>
        </PopoverTrigger>
        <PopoverContent className="w-64 text-sm" collisionPadding={12}>
          <p>{t('template.orphanPopover.body')}</p>
          <button
            type="button"
            onClick={() => { setOpen(false); setRestoreOpen(true); }}
            // Solid underline, NOT dashed: the app's dashed underline means "tap for a
            // popover"; this is an action link inside one.
            className="mt-2 min-h-[44px] cursor-pointer text-left text-brand-600 underline underline-offset-2 hover:text-brand-700"
          >
            {t('template.orphanPopover.restoreAction')}
          </button>
        </PopoverContent>
      </Popover>

      <AlertDialog open={restoreOpen} onOpenChange={setRestoreOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('template.orphanPopover.restoreConfirm.title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('template.orphanPopover.restoreConfirm.body')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('slot.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => unarchive.mutate(slotId, { onSuccess: () => setRestoreOpen(false) })}
              disabled={unarchive.isPending}
            >
              {t('slot.restore')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// Today's ordinary card, now leading with the class's own name (THI-267)
// instead of the bare subject — `slot.group.displayName` is always present
// (every slot has a group after the backfill), tinted by `slot.group.color`
// via `classColorStyle` (null = no tint, today's plain card). Extracted only
// so the day-grid can pick between this and MoveModeSlotCard per slot.
function TimetableSlotCard({
  slot: s, teacher, onOpenDetail, onEdit, templateOrphan, templateMode, hoverState, isHovered, onHoverStart, onHoverEnd,
}: {
  slot: ClassSlotWithDetail;
  teacher: { name: string; color: string } | null;
  onOpenDetail: () => void;
  onEdit: () => void;
  /** True only for an archived slot a template still points at — see
   *  overlayTemplateOnSlots. Renders non-interactive with a warning chip. */
  templateOrphan?: boolean;
  /** True whenever a template (not reality) is selected — used only to
   *  surface inactive-enrollment entries the template would skip on apply;
   *  see template.inactiveChip. */
  templateMode?: boolean;
  /** 'sibling' when another slot of the SAME class is currently hovered
   *  (S3 decision: hovering one khung giờ outlines its siblings too). */
  hoverState: 'none' | 'sibling';
  isHovered: boolean;
  onHoverStart: () => void;
  onHoverEnd: () => void;
}) {
  const { t } = useTranslation('scheduling');
  // Card preview shows only active students; paused ones surface in the popover.
  const activeMembers = s.members.filter((m) => m.status === 'ACTIVE');
  // In template mode, entries pointing at a paused/ended enrollment are
  // rendered too (muted + line-through) so the teacher sees, at a glance,
  // which of the template's placements would be skipped on apply.
  const inactiveMembers = templateMode ? s.members.filter((m) => m.status !== 'ACTIVE') : [];
  // One word per student — the timetable cell is the tightest surface in the
  // app. `displayNames` is what makes one word safe: two students called Ân
  // in the same slot widen to two words so the line does not read "Ân, Ân".
  // The cell still truncates on top of that.
  const roster = displayNames(activeMembers.map((m) => m.studentName)).join(', ');
  // The override carrier (a row whose membershipId equals the đứng-tên
  // teacher's own id, per SlotTeacherRow's doc comment) is not a second
  // teacher — excluded here so a slot with only a pay override on the
  // đứng-tên row doesn't show a false "+1 GV".
  const otherTeacherCount = s.teachers.filter((tr) => tr.membershipId !== s.teacherId).length;
  const tint = classColorStyle(s.group.color);
  const ringColor = tint?.borderColor ?? 'var(--color-brand-500)';
  const ringStyle: CSSProperties | undefined = isHovered
    ? { boxShadow: `0 0 0 2px ${ringColor}, 0 4px 12px rgb(0 0 0 / 0.12)` }
    : hoverState === 'sibling'
      ? { boxShadow: `0 0 0 1.5px ${ringColor}` }
      : undefined;

  if (templateOrphan) {
    return (
      <div className="relative rounded-md border border-dashed border-attention bg-card p-2.5 opacity-60">
        <OrphanSlotPopover slotId={s.id} />
        <div className="flex items-baseline gap-1.5 pr-20">
          <span className="truncate text-[13px] font-semibold text-surface-600">{s.group.displayName}</span>
        </div>
        <div className="mt-0.5 flex items-baseline gap-1 text-[12px]">
          <span className="tabular font-semibold text-surface-900">{s.startTime}</span>
          <span className="text-surface-400">{s.durationMin} {t('slot.minutes')}</span>
        </div>
        {teacher ? (
          <div className="mt-1 flex items-center gap-1.5 text-[12px] text-surface-500">
            <span className="inline-block h-2 w-2 shrink-0 rounded-full" style={{ background: teacher.color }} />
            <span className="truncate">{teacher.name}</span>
            {otherTeacherCount > 0 ? (
              <span className="ml-1 shrink-0 rounded-sm bg-surface-100 px-1 text-[11px] text-surface-500">
                {t('slot.moreTeachers', { count: otherTeacherCount })}
              </span>
            ) : null}
          </div>
        ) : null}
        <div className="mt-1 flex items-center gap-1 text-[12px] text-surface-400">
          <Users className="h-3 w-3 shrink-0" strokeWidth={1.8} />
          {(() => {
            const cap = capacityDisplay(activeMembers.length, s.maxCapacity, t);
            return cap.pill ? (
              <span className="inline-flex h-[19.2px] items-center rounded-full bg-surface-100 px-1.5 text-[11px] font-semibold text-surface-600 tabular">{cap.text}</span>
            ) : (
              <span className="tabular">{cap.text}</span>
            );
          })()}
          <span>HV</span>
        </div>
        <div className="mt-0.5 truncate text-[12px] text-surface-400">{roster || '—'}</div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'group relative rounded-md border transition-colors',
        tint ? undefined : 'border-surface-200 bg-card hover:border-brand-500/40 hover:bg-brand-50/50',
      )}
      style={tint ? { background: tint.background, borderColor: tint.borderColor, ...ringStyle } : ringStyle}
      onMouseEnter={onHoverStart}
      onMouseLeave={onHoverEnd}
    >
      <button
        type="button"
        onClick={onOpenDetail}
        className="block w-full cursor-pointer rounded-md p-2.5 text-left focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      >
        <div className="flex items-baseline gap-1.5 pr-6">
          <span className="truncate text-[13px] font-semibold" style={tint ? { color: tint.color } : { color: 'var(--color-surface-900)' }}>
            {s.group.displayName}
          </span>
        </div>
        <div className="mt-0.5 flex items-baseline gap-1 text-[12.5px]">
          <span className="tabular font-semibold text-surface-900">{s.startTime}</span>
          <span className="text-surface-400">{s.durationMin} {t('slot.minutes')}</span>
        </div>
        {teacher ? (
          <div className="mt-1 flex items-center gap-1.5 text-[12px] text-surface-500">
            <span className="inline-block h-2 w-2 shrink-0 rounded-full" style={{ background: teacher.color }} />
            <span className="truncate">{teacher.name}</span>
            {otherTeacherCount > 0 ? (
              <span className="ml-1 shrink-0 rounded-sm bg-surface-100 px-1 text-[11px] text-surface-500">
                {t('slot.moreTeachers', { count: otherTeacherCount })}
              </span>
            ) : null}
          </div>
        ) : null}
        <div className="mt-1 flex items-center gap-1 text-[12px] text-surface-400">
          <Users className="h-3 w-3 shrink-0" strokeWidth={1.8} />
          {(() => {
            const cap = capacityDisplay(activeMembers.length, s.maxCapacity, t);
            return cap.pill ? (
              <span className="inline-flex h-[19.2px] items-center rounded-full bg-surface-100 px-1.5 text-[11px] font-semibold text-surface-600 tabular">{cap.text}</span>
            ) : (
              <span className="tabular">{cap.text}</span>
            );
          })()}
          <span>HV</span>
        </div>
        <div className="mt-0.5 truncate text-[12px] text-surface-400">{roster || '—'}</div>
        {inactiveMembers.length > 0 ? (
          <div className="mt-0.5 flex flex-wrap gap-x-1.5 text-[12px] text-surface-400">
            {inactiveMembers.map((m) => (
              <span key={m.enrollmentId} title={t('template.inactiveChip')} className="line-through">{m.studentName}</span>
            ))}
          </div>
        ) : null}
      </button>
      <div className="absolute right-1 top-1 transition-opacity sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100">
        <SlotActionsMenu slot={s} onEdit={onEdit} />
      </div>
    </div>
  );
}

// A first-time teacher tried to drag before ticking anyone — the gating was
// correct, the guidance was not: a low, grey caption under the header, easy
// to miss. This banner takes attention styling (same tokens as the
// chốt-sổ nudges) and carries exactly the one thing to do next. It derives
// its state from the draft's own fields — nothing here re-decides what
// "ticked" or "targeted" means, `selected.length` / `targetSlotId` already
// say that. Once a target is staged the confirm bar is doing the guidance
// (target shown, Xác nhận enabled), so the banner steps back entirely
// rather than competing with it.
function MoveModeHint({ draft, isDesktop }: { draft: SlotMoveDraft; isDesktop: boolean }) {
  const { t } = useTranslation('scheduling');
  if (draft.targetSlotId) return null;
  // The drag gesture only exists on desktop (isDesktop gates dragHandleProps),
  // so the hint must not promise it on a phone — each device gets the one
  // instruction that is actually available to it.
  const key =
    draft.selected.length === 0
      ? 'moveMode.hintStart'
      : isDesktop
        ? 'moveMode.hintTarget'
        : 'moveMode.hintTargetMobile';
  return (
    <div className="mb-4 flex items-start gap-2.5 rounded-md border border-attention/30 bg-attention-tint px-4 py-3">
      <Info className="mt-0.5 h-4 w-4 shrink-0 text-attention-foreground" strokeWidth={1.9} />
      <p className="text-sm text-attention-foreground">{t(key)}</p>
    </div>
  );
}

// The move-mode card: members render as tickable chips instead of the
// comma-joined roster line, and the card itself doubles as a drop target
// (desktop only — see useIsDesktop). All selection/eligibility RULES come
// from slotMoveDraft.ts; this component only wires them to dnd-kit and to
// the checkbox markup.
function MoveModeSlotCard({ slot: s, draft, setDraft, sourceSlot, teacher, isDesktop, isDragActive, templateOrphan, templateMode }: {
  slot: ClassSlotWithDetail;
  draft: SlotMoveDraft;
  setDraft: Dispatch<SetStateAction<SlotMoveDraft>>;
  sourceSlot: ClassSlotWithDetail | null;
  teacher: { name: string; color: string } | null;
  isDesktop: boolean;
  isDragActive: boolean;
  /** True only for an archived slot a template still points at — neither
   *  tickable nor a drop target here (see droppableRegistered/tickable). */
  templateOrphan?: boolean;
  /** True whenever a template (not reality) is selected — surfaces
   *  inactive-enrollment entries inline instead of the real "paused, not
   *  shown here" summary line. */
  templateMode?: boolean;
}) {
  const { t } = useTranslation('scheduling');
  const isSource = draft.sourceSlotId === s.id;
  const isStagedTarget = draft.targetSlotId === s.id;
  const eligibleAsTarget = sourceSlot !== null && isEligibleTarget(toCandidate(sourceSlot), toCandidate(s));
  // "Ineligible" is only a concept once a source exists — before any tick,
  // every card is a plain candidate source and none should look dimmed.
  const dimmedOtherSubject = sourceSlot !== null && !isSource && !eligibleAsTarget;
  const activeMembers = s.members.filter((m) => m.status === 'ACTIVE');
  const inactiveMembers = templateMode ? s.members.filter((m) => m.status !== 'ACTIVE') : [];
  const pausedCount = s.members.filter((m) => m.status === 'PAUSED').length;
  // See TimetableSlotCard's own comment — same override-carrier exclusion.
  const otherTeacherCount = s.teachers.filter((tr) => tr.membershipId !== s.teacherId).length;

  // Ineligible cards never get `setDropRef` attached below — that is what
  // keeps a bad drop inert by construction (the node stays out of dnd-kit's
  // collision map) rather than by an onDrop check that could be wrong. An
  // orphan card (archived slot a template still points at) is never a drop
  // target either — see the "Orphan cards" wire-up.
  const droppableRegistered = isDesktop && eligibleAsTarget && draft.selected.length > 0 && !templateOrphan;
  const { setNodeRef: setDropRef, isOver } = useDroppable({ id: s.id, disabled: !droppableRegistered });

  const draggableEnabled = isDesktop && isSource && draft.selected.length > 0;
  // `attributes` (dnd-kit's role/tabIndex bundle) is deliberately not
  // destructured — see the a11y note where `listeners` is spread below.
  const { listeners, setNodeRef: setDragRef } = useDraggable({ id: `move-${s.id}`, disabled: !draggableEnabled });
  // Grabbing ANY ticked chip drags the whole group: `listeners` (the
  // pointer-down handlers) is spread onto every ticked chip below, all
  // pointing at the same draggable id. The node ref only needs to land on
  // one of them for dnd-kit's own bookkeeping.
  const firstTickedId = activeMembers.find((m) => draft.selected.includes(m.enrollmentId))?.enrollmentId;

  // The staged target previously showed nothing about WHAT would land there
  // — only a ring and a badge, so the draft didn't read as containing the
  // teacher's students (Johnny, testing: "vẫn còn bị không biết là ăn
  // chưa"). `previewMembers` reads the ticked names straight off `sourceSlot`
  // (already a prop, already the source of truth for who's ticked) — no new
  // selection logic, just a lookup keyed by the same `draft.selected` the
  // lib already tracks. Derived purely from `draft`, so a drop and a picker
  // pick produce the identical preview, and re-targeting onto a different
  // card moves it there for free: only the card whose id matches
  // `targetSlotId` ever computes a non-empty list.
  const previewMembers = isStagedTarget && sourceSlot
    ? sourceSlot.members.filter((m) => draft.selected.includes(m.enrollmentId))
    : [];

  // Once a target is staged, the ticked chips on the SOURCE card get a
  // second, subtler cue on top of their existing brand tint: they're about
  // to leave. Paired with the target's dashed "about to arrive" ghosts, one
  // signal per card — this one is opacity only, no new badge or copy, so it
  // doesn't compete with the ring+badge that already marks the target.
  const showLeavingHint = isSource && draft.targetSlotId !== null;

  // A subtle "you could drop here" outline on every other eligible card
  // while a drag is in flight — `isOver` (below) is the stronger "drop here
  // now" signal for whichever one is currently under the pointer.
  const showEligibleDragHint = isDragActive && droppableRegistered && !isOver && !isStagedTarget;

  // One state wins, highest priority first. `isStagedTarget` renders the
  // same ring+tint whether the target came from a drop or from the sheet
  // picker (MoveModeBar's `pick` and this card's drop handler both go
  // through the lib's `setTarget`) — one visual state, two gestures, so the
  // card can't tell them apart.
  const cardStateClass = isOver
    ? 'border-brand-500 ring-2 ring-brand-500/50 bg-brand-50/50'
    : isStagedTarget
    ? 'border-brand-500 ring-2 ring-brand-500/40 bg-brand-50/50'
    : isSource
    ? 'border-brand-500 bg-brand-50/40'
    : showEligibleDragHint
    ? 'border-dashed border-brand-400/70'
    : 'border-surface-200 bg-card';

  return (
    <div
      ref={droppableRegistered ? setDropRef : undefined}
      className={cn(
        'relative rounded-md border p-2.5 transition-colors',
        cardStateClass,
        dimmedOtherSubject && 'opacity-40',
        templateOrphan && 'opacity-60 border-dashed border-attention',
      )}
    >
      {templateOrphan ? <OrphanSlotPopover slotId={s.id} /> : null}
      <div className={cn('flex items-baseline gap-1.5', templateOrphan && 'pr-20')}>
        <span className="truncate text-[13px] font-semibold text-surface-600">{s.group.displayName}</span>
        {isSource ? (
          <Badge variant="success" className="ml-auto shrink-0 text-[10px]">{t('moveMode.sourceBadge')}</Badge>
        ) : isStagedTarget ? (
          <Badge variant="success" className="ml-auto shrink-0 text-[10px]">{t('moveMode.targetBadge')}</Badge>
        ) : dimmedOtherSubject ? (
          <Badge variant="muted" className="ml-auto shrink-0 text-[10px]">{t('moveMode.otherSubject')}</Badge>
        ) : null}
      </div>
      <div className="mt-0.5 flex items-baseline gap-1 text-[12.5px]">
        <span className="tabular font-semibold text-surface-900">{s.startTime}</span>
        <span className="text-surface-400">{s.durationMin} {t('slot.minutes')}</span>
      </div>
      {teacher ? (
        <div className="mt-1 flex items-center gap-1.5 text-[12px] text-surface-500">
          <span className="inline-block h-2 w-2 shrink-0 rounded-full" style={{ background: teacher.color }} />
          <span className="truncate">{teacher.name}</span>
          {otherTeacherCount > 0 ? (
            <span className="ml-1 shrink-0 rounded-sm bg-surface-100 px-1 text-[11px] text-surface-500">
              {t('slot.moreTeachers', { count: otherTeacherCount })}
            </span>
          ) : null}
        </div>
      ) : null}
      {/* Same N/cap the ordinary card shows (TimetableSlotCard, above) — move
          mode must not hide capacity, or the teacher moving a group onto this
          card as a target can't see they're overfilling it. No enforcement:
          nothing else in the app enforces maxCapacity, so this only makes the
          existing number visible, on both the source card and every eligible
          target. */}
      <div className="mt-1 flex items-center gap-1 text-[12px] text-surface-400">
        <Users className="h-3 w-3 shrink-0" strokeWidth={1.8} />
        {(() => {
          const cap = capacityDisplay(activeMembers.length, s.maxCapacity, t);
          return cap.pill ? (
            <span className="inline-flex h-[19.2px] items-center rounded-full bg-surface-100 px-1.5 text-[11px] font-semibold text-surface-600 tabular">{cap.text}</span>
          ) : (
            <span className="tabular">{cap.text}</span>
          );
        })()}
        <span>HV</span>
        {/* "+N" rather than rewriting the count to "N → N+2": it reads as a
            delta on top of the real number, same 12px/tabular type as the
            count itself, and disappears the instant the count itself
            changes (post-confirm refetch), so the two never have to be kept
            in sync by hand. */}
        {previewMembers.length > 0 ? (
          <span className="tabular font-medium text-brand-600">+{previewMembers.length}</span>
        ) : null}
      </div>

      {activeMembers.length > 0 || previewMembers.length > 0 || inactiveMembers.length > 0 ? (
        <div className="mt-2 flex flex-wrap gap-1">
          {activeMembers.map((m) => {
            const ticked = draft.selected.includes(m.enrollmentId);
            // An orphan card (archived slot) is never tickable, template
            // mode or not — see the "Orphan cards" wire-up.
            const tickable = !templateOrphan && canTickInSlot(draft, s.id);
            // `listeners` only — never `attributes`. dnd-kit's `attributes`
            // stamps role="button"/tabIndex=0 onto whatever it's spread on,
            // announcing a keyboard-operable control to screen readers. No
            // KeyboardSensor is registered here (pointer-drag only, see
            // useIsDesktop), so that role would be a lie: nothing responds to
            // a keyboard press on this label. The checkbox inside already
            // carries the real interactive semantics.
            const dragHandleProps = ticked && draggableEnabled
              ? { ...listeners, ref: m.enrollmentId === firstTickedId ? setDragRef : undefined }
              : {};
            return (
              <label
                key={m.enrollmentId}
                {...dragHandleProps}
                className={cn(
                  'flex items-center gap-1.5 rounded-full border px-2 py-1 text-[12px] transition-colors',
                  ticked ? 'border-brand-500 bg-brand-100 text-brand-800' : 'border-surface-200 bg-surface-0 text-surface-700',
                  !tickable && 'cursor-not-allowed opacity-40',
                  ticked && draggableEnabled && 'cursor-grab active:cursor-grabbing',
                  // "sẽ chuyển đi": once a target is staged, the ticked chips
                  // here fade slightly — the mirror of the ghost chips
                  // appearing dashed on the target card below.
                  ticked && showLeavingHint && 'opacity-70',
                )}
              >
                <Checkbox
                  checked={ticked}
                  disabled={!tickable}
                  onCheckedChange={() => setDraft((d) => toggleTick(d, s.id, m.enrollmentId))}
                  className="h-3.5 w-3.5"
                />
                {m.studentName}
              </label>
            );
          })}
          {/* Ghost chips: the same students ticked on the source, previewed
              on whichever card currently holds draft.targetSlotId — dashed
              per this screen's existing "placeholder/pending" meaning for
              dashed (see the empty-day cell, the paused-member rows), and a
              student who will land here on confirm is exactly that: pending,
              not yet real. Not interactive (no checkbox, no drag handle) —
              they represent a fact about the OTHER card's ticks, not a
              control of their own. */}
          {previewMembers.map((m) => (
            <span
              key={`preview-${m.enrollmentId}`}
              title={t('moveMode.previewChipTitle')}
              className="flex items-center gap-1.5 rounded-full border border-dashed border-brand-400/70 bg-brand-50/40 px-2 py-1 text-[12px] text-brand-600"
            >
              {m.studentName}
            </span>
          ))}
          {/* Template mode only: a paused/ended entry still renders here
              (muted, line-through, no checkbox) rather than being folded
              into the pausedNotice count below — the teacher needs to see
              exactly which placement will be skipped on apply, not just how
              many. */}
          {inactiveMembers.map((m) => (
            <span
              key={`inactive-${m.enrollmentId}`}
              title={t('template.inactiveChip')}
              className="flex items-center gap-1.5 rounded-full border border-surface-200 bg-surface-50 px-2 py-1 text-[12px] text-surface-400"
            >
              <span className="line-through">{m.studentName}</span>
            </span>
          ))}
        </div>
      ) : (
        <p className="mt-2 text-[12px] text-surface-400">{t('slot.noMembers')}</p>
      )}
      {pausedCount > 0 && !templateMode ? (
        <p className="mt-1.5 text-[11px] text-surface-400">{t('moveMode.pausedNotice', { count: pausedCount })}</p>
      ) : null}
    </div>
  );
}

// The bottom bar + target-picker sheet. Renders nothing until something is
// ticked. `pick` (sheet row click) and the grid's drop handler both call the
// lib's `setTarget` with the same arguments, so this component never needs
// its own idea of "did the target come from a drag or a tap".
function MoveModeBar({ slots, draft, setDraft, sourceSlot, moveMutation, templateId, template, saveEntries }: {
  slots: ClassSlotWithDetail[];
  draft: SlotMoveDraft;
  setDraft: Dispatch<SetStateAction<SlotMoveDraft>>;
  sourceSlot: ClassSlotWithDetail | null;
  moveMutation: ReturnType<typeof useMoveSlotMembers>;
  /** null = reality — confirm calls `moveMutation` exactly as before. Set =
   *  editing a template — confirm rewrites `template.entries` through
   *  `applyMoveToEntries` and saves via `saveEntries` instead; no real
   *  membership is touched. */
  templateId: string | null;
  template: ScheduleTemplateDetail | undefined;
  saveEntries: ReturnType<typeof useSaveTemplateEntries>;
}) {
  const { t } = useTranslation('scheduling');
  const [pickerOpen, setPickerOpen] = useState(false);

  if (draft.selected.length === 0 || !sourceSlot) return null;

  const eligible = slots.filter((s) => isEligibleTarget(toCandidate(sourceSlot), toCandidate(s)));
  const target = draft.targetSlotId ? slots.find((s) => s.id === draft.targetSlotId) ?? null : null;

  const pick = (candidate: ClassSlotWithDetail) => {
    setDraft((d) => setTarget(d, toCandidate(sourceSlot), toCandidate(candidate)));
    setPickerOpen(false);
  };

  const confirm = () => {
    if (!draft.targetSlotId) return;
    const move = { fromSlotId: sourceSlot.id, toSlotId: draft.targetSlotId, enrollmentIds: [...draft.selected] };
    if (templateId !== null && template) {
      saveEntries.mutate(
        { entries: applyMoveToEntries(template.entries, move) },
        { onSuccess: () => setDraft(clearDraft()) },
      );
      return;
    }
    moveMutation.mutate(move, { onSuccess: () => setDraft(clearDraft()) });
  };

  return (
    <SelectionBar
      id="move-mode"
      ariaLabel={t('moveMode.bar.ariaLabel')}
      active
      lowered={pickerOpen}
      summary={
        <>
          <span>{t('moveMode.bar.count', { count: draft.selected.length })}</span>
          <Button type="button" variant="outline" size="sm" onClick={() => setPickerOpen(true)}>
            {target ? `${t(`days.${target.dayOfWeek}`)} ${target.startTime} · ${target.group.displayName}` : t('moveMode.bar.pickTarget')}
          </Button>
        </>
      }
      actions={
        <>
          <Button type="button" variant="ghost" size="sm" onClick={() => setDraft(clearDraft())}>
            {t('moveMode.bar.cancel')}
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={!draft.targetSlotId || moveMutation.isPending || saveEntries.isPending}
            onClick={confirm}
          >
            {moveMutation.isPending || saveEntries.isPending ? t('moveMode.bar.confirming') : t('moveMode.bar.confirm')}
          </Button>
        </>
      }
    >
      <Sheet open={pickerOpen} onOpenChange={setPickerOpen}>
        <SheetContent side="bottom" className="max-h-[70vh] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{t('moveMode.picker.title')}</SheetTitle>
            <SheetDescription>{t('moveMode.picker.description')}</SheetDescription>
          </SheetHeader>
          <div className="mt-4 space-y-2">
            {eligible.length === 0 ? (
              <p className="text-sm text-surface-400">{t('moveMode.picker.empty')}</p>
            ) : (
              eligible.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => pick(s)}
                  className={cn(
                    'flex w-full items-center justify-between rounded-md border px-3 py-2 text-left text-sm transition-colors hover:border-brand-500/50 hover:bg-brand-50/50',
                    draft.targetSlotId === s.id ? 'border-brand-500 bg-brand-50' : 'border-surface-200',
                  )}
                >
                  <span>
                    <span className="font-medium">{t(`days.${s.dayOfWeek}`)} {s.startTime}</span>
                    <span className="text-surface-500"> · {s.group.displayName}</span>
                  </span>
                  <span className="tabular text-surface-400">{s.members.filter((m) => m.status === 'ACTIVE').length} HV</span>
                </button>
              ))
            )}
          </div>
        </SheetContent>
      </Sheet>
    </SelectionBar>
  );
}

function TimetableSkeleton() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-x-3 gap-y-5 lg:gap-3">
      {DAYS.map((d) => (
        <div key={d}>
          <div className="mb-2.5 h-4 border-b border-surface-200 pb-1.5">
            <div className="h-2.5 w-7 rounded bg-surface-200" />
          </div>
          <div className="space-y-2">
            {Array.from({ length: (d % 3) + 1 }).map((_, i) => (
              <div key={i} className="h-[68px] animate-pulse rounded-md border border-surface-200 bg-surface-100/70" />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// Slot detail: full official-member roster (the timetable card truncates
// it). Read-only for a MEMBER (`useCan('manage:slots')`); for anyone who can
// manage slots, the roster is now editable in BOTH modes — reality (real
// join/leave via useJoinSlot/useLeaveSlot) and template (rewrites
// `template.entries` through the same PUT flow move-mode's confirm already
// uses, see MoveModeBar's `confirm` above). `slot` here is looked up fresh
// from `gridSlots` by the parent on every render (see `detailSlot` above),
// so an add/remove made while this dialog is open re-renders it immediately
// — no separate refetch-and-reopen dance.
function SlotDetailDialog({ slot, teacher, members, open, onOpenChange, templateId, template, saveEntries }: {
  slot: ClassSlotWithDetail | null;
  teacher: { name: string; color: string } | null;
  /** All org members — resolves the OTHER teachers' names/colours below the
   *  đứng-tên line (full list only in this detail surface, per spec §3). */
  members: TeacherLike[];
  open: boolean;
  onOpenChange: (o: boolean) => void;
  /** null = reality mode — remove/add call the real leave/join endpoints.
   *  Set = editing a template — remove/add instead rewrite `template.entries`
   *  via `saveEntries`, and no real membership is touched (same reality/
   *  template split as MoveModeBar's `confirm`). */
  templateId: string | null;
  template: ScheduleTemplateDetail | undefined;
  saveEntries: ReturnType<typeof useSaveTemplateEntries>;
}) {
  const { t } = useTranslation('scheduling');
  const { t: tProgress } = useTranslation('progress');
  const serverNow = useServerNow(); // follows the (maybe simulated) server clock, not the browser's
  const canManageSlots = useCan('manage:slots');
  const isTemplateMode = templateId !== null;
  // Both hooks are created unconditionally (rules of hooks) even while the
  // dialog is closed / `slot` is null — same `?? ''` pattern SessionDetailPage
  // uses for its own per-id mutations.
  const join = useJoinSlot(slot?.id ?? '');
  const leave = useLeaveSlot(slot?.id ?? '');
  // Only fetched for someone who can actually add a member — no point paying
  // for the request behind a control that's hidden anyway.
  const { data: subjectEnrollments } = useEnrollmentsForSubject(canManageSlots ? (slot?.subjectId ?? null) : null);

  const [removeTarget, setRemoveTarget] = useState<{ enrollmentId: string; studentName: string } | null>(null);

  const currentMemberIds = useMemo(() => new Set((slot?.members ?? []).map((m) => m.enrollmentId)), [slot]);
  const addOptions = useMemo(
    () => (subjectEnrollments ?? []).filter((e) => !currentMemberIds.has(e.id)),
    [subjectEnrollments, currentMemberIds],
  );
  const addPending = join.isPending || saveEntries.isPending;
  const removePending = leave.isPending || saveEntries.isPending;

  const handleAdd = (enrollmentId: string) => {
    if (!slot) return;
    if (isTemplateMode && template) {
      saveEntries.mutate({ entries: addEntryToEntries(template.entries, { slotId: slot.id, enrollmentId }) });
    } else {
      join.mutate({ enrollmentId });
    }
  };

  const handleRemove = () => {
    if (!slot || !removeTarget) return;
    if (isTemplateMode && template) {
      saveEntries.mutate(
        { entries: removeEntryFromEntries(template.entries, { slotId: slot.id, enrollmentId: removeTarget.enrollmentId }) },
        { onSuccess: () => setRemoveTarget(null) },
      );
      return;
    }
    leave.mutate(removeTarget.enrollmentId, { onSuccess: () => setRemoveTarget(null) });
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="sm:max-w-[420px]"
        >
          {slot ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-baseline gap-1.5">
                  <span className="text-surface-500">{t(`days.${slot.dayOfWeek}`)}</span>
                  <span className="tabular font-semibold text-surface-900">{slot.startTime}</span>
                  <span className="text-surface-400">·</span>
                  <span className="font-semibold text-surface-900">{slot.group.displayName}</span>
                </DialogTitle>
                {/* Always rendered — the window this implements is PER TARGET
                    MONTH, not a visibility toggle: the button for month M is
                    "visible from the 25th of M through the whole of M+1"
                    (handoff: "hiện từ ngày 25 của tháng và cả tháng sau").
                    `reviewMonthFor` already picks that same M (previous month
                    on days 1-24, current month from the 25th on), so
                    rendering unconditionally and only ever changing the
                    TARGET is exactly how that per-month window is satisfied —
                    a day>=25 render gate would instead HIDE this on days 1-24
                    of M+1, which is precisely when teachers are still writing
                    M's reviews (fix wave C2, controller ruling reverting the
                    gate this comment used to describe). */}
                <Link
                  to={`/subjects/${slot.subjectId}/monthly-reviews?month=${reviewMonthFor(serverNow)}`}
                  className="mt-2 inline-flex min-h-[44px] items-center text-[12.5px] font-medium text-brand-600 hover:underline"
                >
                  {tProgress('review.entryButton', { month: formatMonthKey(reviewMonthFor(serverNow)) })}
                </Link>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                {teacher ? (
                  <div className="flex items-center gap-2 text-surface-500">
                    <span className="inline-block h-2 w-2 shrink-0 rounded-full" style={{ background: teacher.color }} />
                    {teacher.name}
                  </div>
                ) : null}
                {/* Full list only here (spec §3) — the cards above stay one
                    name, one colour, plus a muted "+n GV". Excludes the
                    override carrier (membershipId === slot.teacherId), which
                    is pay data on the đứng-tên row above, not a second
                    teacher — see SlotTeacherRow's doc comment. */}
                {slot.teachers
                  .filter((tr) => tr.membershipId !== slot.teacherId)
                  .map((tr) => {
                    const other = resolveTeacher(members, tr.membershipId);
                    if (!other) return null;
                    return (
                      <div key={tr.membershipId} className="flex items-center gap-2 text-surface-500">
                        <span className="inline-block h-2 w-2 shrink-0 rounded-full" style={{ background: other.color }} />
                        <span className="truncate">{other.name}</span>
                        <span className="text-surface-400">
                          · {tr.role === 'CHINH' ? t('slot.roleChinh') : t('slot.roleTroGiang')}
                        </span>
                      </div>
                    );
                  })}
                <div className="tabular text-surface-500">
                  {slot.durationMin} {t('slot.minutes')} · {slot.memberCount}{slot.maxCapacity ? `/${slot.maxCapacity}` : ''} HV
                </div>
                <SlotMemberList
                  slot={slot}
                  canManage={canManageSlots}
                  onRemove={(m) => setRemoveTarget(m)}
                  addOptions={addOptions}
                  onAdd={handleAdd}
                  addPending={addPending}
                />
              </div>
            </>
          ) : null}
          {/* A JSX DESCENDANT of DialogContent on purpose. As a sibling, taps
              on the confirm's buttons killed BOTH dialogs on touch devices:
              the tap's delayed synthesized mouse event landed after the
              confirm had closed, hit this dialog's overlay, and dismissed it
              — and an interact-outside guard keyed on the confirm being open
              had already been lifted by then (mouse clicks were fine, which
              is why desktop QA missed it). As a descendant, Radix tracks the
              portalled confirm as "inside" this dialog — same reason the
              AddStudentCombobox popover never had the bug. No guards needed.
              Reproduced and verified both paths via CDP touch emulation. */}
      <AlertDialog open={removeTarget !== null} onOpenChange={(o) => { if (!o) setRemoveTarget(null); }}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {isTemplateMode ? t('slot.removeConfirm.titleTemplate') : t('slot.removeConfirm.title')}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {isTemplateMode
                    ? t('slot.removeConfirm.bodyTemplate', { name: removeTarget?.studentName ?? '' })
                    : t('slot.removeConfirm.bodyReality', { name: removeTarget?.studentName ?? '' })}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t('slot.cancel')}</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleRemove}
                  disabled={removePending}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {t('slot.removeConfirm.action')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </DialogContent>
      </Dialog>

    </>
  );
}

// Slot roster: active students (linked to detail, each with a remove
// button) + a collapsed "paused" group + an "Add student" link that reveals
// AddStudentCombobox. `canManage` hides all three controls at once for a
// MEMBER — the list stays exactly what it was before this feature (read-only,
// no remove button, no add link).
function SlotMemberList({ slot, canManage, onRemove, addOptions, onAdd, addPending }: {
  slot: ClassSlotWithDetail;
  canManage: boolean;
  onRemove: (m: { enrollmentId: string; studentName: string }) => void;
  addOptions: EnrollmentWithSubject[];
  onAdd: (enrollmentId: string) => void;
  addPending: boolean;
}) {
  const { t } = useTranslation('scheduling');
  const [pausedOpen, setPausedOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const pausedListId = `slot-paused-${slot.id}`;
  const active = slot.members.filter((m) => m.status === 'ACTIVE');
  const paused = slot.members.filter((m) => m.status === 'PAUSED');

  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-surface-400">
          {t('slot.officialMembers')} ({active.length})
        </span>
        {canManage ? (
          <button
            type="button"
            onClick={() => setAddOpen((o) => !o)}
            className="flex min-h-[44px] items-center px-1 text-xs font-medium text-brand-600 transition-colors hover:text-brand-700"
          >
            {t('slot.addMember')}
          </button>
        ) : null}
      </div>

      {addOpen ? (
        <div className="mb-2">
          <AddStudentCombobox
            options={addOptions}
            onSelect={(enrollmentId) => { onAdd(enrollmentId); setAddOpen(false); }}
            disabled={addPending}
            placeholder={t('slot.addMemberPlaceholder')}
          />
        </div>
      ) : null}

      {active.length > 0 ? (
        <ul className="space-y-1">
          {active.map((m) => (
            <li key={m.enrollmentId} className="flex items-stretch gap-1.5">
              <Link
                to={`/students/${m.studentId}`}
                className="flex flex-1 items-center gap-2 rounded-md border border-surface-200 bg-surface-0 px-2.5 py-1.5 transition-colors hover:border-brand-500/40 hover:text-brand-700"
              >
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-brand-500" />
                {m.studentName}
              </Link>
              {canManage ? (
                <button
                  type="button"
                  aria-label={t('slot.removeAria', { name: m.studentName })}
                  onClick={() => onRemove({ enrollmentId: m.enrollmentId, studentName: m.studentName })}
                  className="flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded-md border border-surface-200 text-surface-400 transition-colors hover:border-destructive/40 hover:text-destructive"
                >
                  <X className="h-4 w-4" strokeWidth={1.8} />
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-surface-400">{t('slot.noMembers')}</p>
      )}

      {paused.length > 0 && (
        <div className="mt-3">
          <button
            type="button"
            onClick={() => setPausedOpen((o) => !o)}
            aria-expanded={pausedOpen}
            aria-controls={pausedListId}
            className="flex items-center gap-1.5 text-xs font-medium text-surface-400 hover:text-surface-700"
          >
            <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', pausedOpen && 'rotate-180')} />
            {t('slot.pausedSection')} ({paused.length})
          </button>
          {pausedOpen && (
            <ul id={pausedListId} className="mt-1.5 space-y-1">
              {paused.map((m) => (
                <li key={m.enrollmentId}>
                  <Link
                    to={`/students/${m.studentId}`}
                    className="flex items-center gap-2 rounded-md border border-dashed border-surface-200 bg-surface-50 px-2.5 py-1.5 text-surface-400 transition-colors hover:border-brand-500/30"
                  >
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-surface-300" />
                    <span className="line-through">{m.studentName}</span>
                    <span className="text-[11px]">({t('slot.pausedTag')})</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// Owns its own open-state so archiving one row's confirm dialog never opens another row's.
function SlotActionsMenu({ slot, onEdit }: { slot: ClassSlotWithDetail; onEdit: () => void }) {
  const { t } = useTranslation('scheduling');
  const archive = useArchiveSlot();
  const unarchive = useUnarchiveSlot();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const isArchived = slot.status === 'ARCHIVED';
  const canManageSlots = useCan('manage:slots');

  const handleArchive = () => {
    archive.mutate(slot.id, { onSuccess: () => setConfirmOpen(false) });
  };

  return (
    <>
      <AdminOnlyTooltip show={!canManageSlots}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 text-surface-400 hover:text-surface-700"
            disabled={!canManageSlots}
          >
            <MoreVertical className="h-3.5 w-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={onEdit} className="cursor-pointer">
            <Pencil className="w-3.5 h-3.5 mr-2" /> {t('slot.edit')}
          </DropdownMenuItem>
          {isArchived ? (
            <DropdownMenuItem
              onClick={() => unarchive.mutate(slot.id)}
              disabled={unarchive.isPending}
              className="cursor-pointer"
            >
              <RotateCcw className="w-3.5 h-3.5 mr-2" /> {t('slot.restore')}
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem
              onClick={() => setConfirmOpen(true)}
              className="cursor-pointer text-destructive focus:bg-destructive/10 focus:text-destructive"
            >
              <Archive className="w-3.5 h-3.5 mr-2" /> {t('slot.archive')}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      </AdminOnlyTooltip>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('slot.archiveConfirm.title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('slot.archiveConfirm.body')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('slot.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleArchive}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('slot.archiveConfirm.action')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
